import { forwardRef } from 'react';
import {
  Box,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
} from '@optiaxiom/react';

import { MarkdownText } from '@frontend/components/MarkdownText';
import { useOptimizelyUser } from '@frontend/hooks';

type TokenUsage = {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  thinking_tokens?: number;
  cached_tokens?: number;
};

type TokenUsageSectionProps = {
  tokenUsage?: TokenUsage;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const TokenUsageSection = forwardRef<HTMLDivElement, TokenUsageSectionProps>(
  function TokenUsageSection({ tokenUsage, open, onOpenChange }, ref) {
    const { isOptimizelyUser } = useOptimizelyUser();

    if (!isOptimizelyUser || !tokenUsage) {
      return null;
    }
    const hasValidTokenUsage =
      tokenUsage &&
      typeof tokenUsage.input_tokens === 'number' &&
      typeof tokenUsage.output_tokens === 'number' &&
      typeof tokenUsage.total_tokens === 'number';

    if (!isOptimizelyUser || !hasValidTokenUsage) {
      return null;
    }

    return (
      <Box ref={ref}>
        <Disclosure open={open} onOpenChange={onOpenChange}>
          <DisclosureTrigger>
            <Field label="Token Usage" info="Token consumption details for this execution" />
          </DisclosureTrigger>
          <DisclosureContent>
            <Flex flexDirection="column" gap="16" style={{ paddingInline: '25px' }}>
              <Box bg="bg.default" rounded="md">
                <MarkdownText
                  text={(() => {
                    const rows = [
                      `| **Input Tokens** | ${tokenUsage?.input_tokens?.toLocaleString()} |`,
                      `| **Output Tokens** | ${tokenUsage?.output_tokens?.toLocaleString()} |`,
                    ];

                    if ((tokenUsage?.thinking_tokens ?? 0) > 0) {
                      rows.push(
                        `| **Thinking Tokens** | ${tokenUsage?.thinking_tokens?.toLocaleString()} |`
                      );
                    }

                    if ((tokenUsage?.cached_tokens ?? 0) > 0) {
                      rows.push(
                        `| **Cached Tokens** | ${tokenUsage?.cached_tokens?.toLocaleString()} |`
                      );
                    }

                    rows.push(
                      `| **Total Tokens** | ${tokenUsage?.total_tokens?.toLocaleString()} |`
                    );

                    return `| Metric | Count |\n|--------|-------|\n${rows.join('\n')}`;
                  })()}
                />
              </Box>
            </Flex>
          </DisclosureContent>
        </Disclosure>
      </Box>
    );
  }
);
